namespace $ {

	function $shm_hitalama_process_mem_value< Result >( atom: $mol_wire_atom< any, any, Result > ) {
		if( !$mol_wire_fiber.warm ) return atom.result() as Result

		if( $mol_wire_auto()?.temp ) {
			return atom.once() as Result
		} else {
			return atom.sync() as Result
		}
	}

	function $shm_hitalama_process_mem_wait< Result >(
		process: $shm_hitalama_process< Result >,
		atom: $mol_wire_atom< any, any, Result >,
		wait: PromiseLike< unknown >,
	) {
		void ( async()=> {
			try {
				await wait
			} catch( error ) {
				if( process.data_status() === 'running' ) $shm_hitalama_process_wire_fail( process, error )
				return
			}

			while( process.data_status() === 'running' ) {
				try {
					const next = $shm_hitalama_process_mem_value( atom )
					if( next instanceof Error ) {
						$shm_hitalama_process_wire_fail( process, next )
					} else {
						$shm_hitalama_process_wire_done( process, next )
					}
					return next
				} catch( error ) {
					if( $mol_promise_like( error ) ) {
						try {
							await error
						} catch( inner ) {
							if( process.data_status() === 'running' ) $shm_hitalama_process_wire_fail( process, inner )
							return
						}
						continue
					}

					$shm_hitalama_process_wire_fail( process, error )
					return
				}
			}
		} )()
	}

	export function $shm_hitalama_process_mem< Args extends readonly unknown[], Result >(
		host: object,
		field: string,
		descr?: TypedPropertyDescriptor< ( ... args: Args )=> Result >,
	) {
		type Host = typeof host

		const process_by_atom = new WeakMap<
			$mol_wire_atom< Host, Args, Result >,
			$shm_hitalama_process< Result >
		>

		let method!: Function
		const init = $shm_hitalama_process_wire_atom_task(
			host,
			field,
			descr as TypedPropertyDescriptor< $shm_hitalama_process_wire_task< Host, Args, Result > > | undefined,
			process_by_atom,
			( host, args, atom )=> {
				const process_prev = process_by_atom.get( atom )
				const restart = !process_prev
					|| args[0] !== undefined
					|| process_prev.data_status() !== 'running'

				return $shm_hitalama_process_wire_atom_process(
					host,
					method,
					field,
					args,
					atom,
					process_by_atom,
					restart,
				)
			},
		)

		const descr2 = {
			... init.descr,
			value: function( this: Host, ... args: Args ) {
				const atom = $mol_wire_atom.solo( this, init.task )
				const process_prev = process_by_atom.get( atom )
				const cache_hit = ( atom as any ).cursor === $mol_wire_cursor.fresh && !$mol_promise_like( atom.cache )

				$shm_hitalama_process.touch( this, descr2.value )

				if(( args.length === 0 )||( args[0] === undefined )) {
					if( cache_hit ) return $shm_hitalama_process_mem_value( atom )
				}

				const restart = !process_prev
					|| args[0] !== undefined
					|| ( $mol_wire_fiber.warm && process_prev.data_status() !== 'running' )
				const process = $shm_hitalama_process_wire_atom_process(
					this,
					descr2.value,
					field,
					args,
					atom,
					process_by_atom,
					restart,
				)

				if(( args.length === 0 )||( args[0] === undefined )) {
					try {
						return $shm_hitalama_process_wire_result( process, ()=> $shm_hitalama_process_mem_value( atom ) )
					} catch( error ) {
						if( $mol_promise_like( error ) ) $shm_hitalama_process_mem_wait( process, atom, error )
						throw error
					}
				}

				return $shm_hitalama_process_wire_result( process, ()=> atom.resync( args ) as Result )
			},
		}

		Reflect.defineProperty( descr2.value, 'name', { value: init.orig.name + ' ' } )
		Reflect.defineProperty( descr2.value, 'length', { value: init.orig.length } )
		Object.assign( descr2.value, { orig: init.orig } )
		method = descr2.value

		Reflect.defineProperty( host, field, descr2 )

		return descr2 as any
	}

	export function $shm_hitalama_process_mem_key< Args extends [ unknown, ... unknown[] ], Result >(
		host: object,
		field: string,
		descr?: TypedPropertyDescriptor< ( ... args: Args )=> Result >,
	) {
		type Host = typeof host

		const process_by_atom = new WeakMap<
			$mol_wire_atom< Host, Args, Result >,
			$shm_hitalama_process< Result >
		>

		let method!: Function
		const init = $shm_hitalama_process_wire_atom_task(
			host,
			field,
			descr as TypedPropertyDescriptor< $shm_hitalama_process_wire_task< Host, Args, Result > > | undefined,
			process_by_atom,
			( host, args, atom )=> {
				const process_prev = process_by_atom.get( atom )
				const restart = !process_prev
					|| args[1] !== undefined
					|| process_prev.data_status() !== 'running'

				return $shm_hitalama_process_wire_atom_process(
					host,
					method,
					field,
					args,
					atom,
					process_by_atom,
					restart,
				)
			},
		)

		const descr2 = {
			... init.descr,
			value: function( this: Host, ... args: Args ) {
				const atom = $mol_wire_atom.plex( this, init.task, args[0] )
				const process_prev = process_by_atom.get( atom )
				const cache_hit = ( atom as any ).cursor === $mol_wire_cursor.fresh && !$mol_promise_like( atom.cache )

				$shm_hitalama_process.touch( this, descr2.value )

				if(( args.length === 1 )||( args[1] === undefined )) {
					if( cache_hit ) return $shm_hitalama_process_mem_value( atom )
				}

				const restart = !process_prev
					|| args[1] !== undefined
					|| ( $mol_wire_fiber.warm && process_prev.data_status() !== 'running' )
				const process = $shm_hitalama_process_wire_atom_process(
					this,
					descr2.value,
					field,
					args,
					atom,
					process_by_atom,
					restart,
				)

				if(( args.length === 1 )||( args[1] === undefined )) {
					try {
						return $shm_hitalama_process_wire_result( process, ()=> $shm_hitalama_process_mem_value( atom ) )
					} catch( error ) {
						if( $mol_promise_like( error ) ) $shm_hitalama_process_mem_wait( process, atom, error )
						throw error
					}
				}

				return $shm_hitalama_process_wire_result( process, ()=> atom.resync( args ) as Result )
			},
		}

		Reflect.defineProperty( descr2.value, 'name', { value: init.orig.name + ' ' } )
		Reflect.defineProperty( descr2.value, 'length', { value: init.orig.length } )
		Object.assign( descr2.value, { orig: init.orig } )
		method = descr2.value

		Reflect.defineProperty( host, field, descr2 )

		return descr2 as any
	}

}
